import { CollectionModel, FormModel, IkeCSV, IkeCSVMapper, type IkeCSVMapping } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles, DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import { IkeTrigger } from '../../services/util/trigger';
import { AppStore } from '../../store';

export const CSS = {
	container: 'c-ImportMapper',
	spinner: 'c-ImportMapper__spinner',
	header: 'c-ImportMapper__header',
	body: 'c-ImportMapper__body',

	isInvalid: 'c-ImportMapper--invalid',
};

addStyles({
	[CSS.spinner]: {
		margin: 'auto',
		padding: DefaultTheme.space(2),
		display: 'flex',
		'justify-content': 'center',
	},
	[CSS.container]: {
		padding: '8px',
		width: '100%',
		'table-layout': 'fixed',
		'font-family': DefaultTheme.font.familyCondensed,
	},
	[CSS.body + ' td']: {
		padding: DefaultTheme.space(1),
		'max-width': 0,
		overflow: 'hidden',
		'min-width': 0,
		'white-space': 'nowrap',
		'text-overflow': 'ellipsis',
	},
	[CSS.isInvalid]: {
		color: 'red',
	},
});

export class CSVFormImportControl extends IMithrilStateComponent {
	trigger: IkeTrigger = new IkeTrigger('CSVImport');
	@observable formId: string | null = null;

	data: IkeCSV = null;
	form: FormModel = null;

	mappings: { mapping: IkeCSVMapping; selection: SelectInputState }[] = [];
	mapper: IkeCSVMapper;

	@observable isLoading = false;
	@observable isValid = false;

	constructor() {
		super();

		makeObservable(this);
	}

	@action setLoaded() {
		this.isValid = this.mapper.isValid;
		this.isLoading = false;
	}

	get collections(): CollectionModel[] {
		return (this.mapper != null && this.mapper.processed) || [];
	}

	@action process(formId: string, data: IkeCSV) {
		if (!data.isValid) {
			throw new Error('Invalid CSV given');
		}
		this.isLoading = true;
		this.formId = formId;
		this.data = data;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.loadFormAndProcess();
	}

	private async loadFormAndProcess() {
		const form = await AppStore.Form.fetch(this.formId);
		this.form = form;
		this.mapper = new IkeCSVMapper(this.data, form);
		this.mapper.setDefaultMappings();

		this.mappings = [];
		for (const mapping of this.mapper.mappings) {
			const displayName = mapping.fieldKey
				? mapping.field.displayName + ' ' + Translate(mapping.fieldKey.toUpperCase())
				: mapping.field.displayName;
			const selection = new SelectInputState(displayName);
			this.reaction(
				() => selection.value,
				(headerKey) => this.setMapping(mapping, headerKey)
			);

			for (const header of this.data.headers) {
				selection.addOption({ key: header, value: header });
			}

			selection._value = this.data.headers[mapping.csvIndex] || null;
			this.mappings.push({ mapping, selection });
		}
		this.setLoaded();
	}

	get nodes() {
		if (this.isLoading || this.form == null) {
			return m('div', { className: CSS.spinner }, Spinner.view());
		}
		this.trigger.observe();

		return m('table', { className: CSS.container }, [
			m(
				'thead',
				{ className: CSS.header },
				this.mappings.map((h) => m('th', SelectInputComponent.view(h.selection)))
			),
			m(
				'tbody',
				{ className: CSS.body },
				this.collections.map((r, i) => this.viewRow(i))
			),
		]);
	}

	viewRow(index: number) {
		const children = [];
		let hasValue = false;
		for (const mapping of this.mappings) {
			if (mapping == null || mapping.selection.value == null) {
				children.push(m('td'));
			} else {
				hasValue = true;
				const mappedValue = this.mapper.getCollectionValue(mapping.mapping, index);
				if (mappedValue.isValid) {
					children.push(m('td', mappedValue.value));
				} else {
					this.isValid = false;
					children.push(
						m(
							'td',
							{
								className: CSS.isInvalid,
								title: Translate(T.COLLECTION_IMPORT_FIELD_INVALID, {
									field: mapping.mapping.field.displayName,
								}),
							},
							mappedValue.raw
						)
					);
				}
			}
		}

		if (hasValue) {
			return m('tr', {}, children);
		}
		return null;
	}

	@action setMapping(mapping: IkeCSVMapping, csvHeader: null | string) {
		const headerIndex = this.data.getHeaderIndex(csvHeader);
		this.mapper.setMapping(mapping, headerIndex);
		this.isValid = this.mapper.isValid;
		// Force a redraw
		this.trigger.trigger();
	}

	@action reset() {
		this.formId = null;
		this.data = null;
		this.isLoading = true;
		this.isValid = false;
		this.mappings = [];
	}
}
